import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useContextMenu } from "../Layout Component/ContextMenu";
import { setLoader } from "../Store/Reducers/UserReducer";
import {
  GetQuestionairebyPatientId,
  getAllergies,
  getAllfertilityTreat,
  getAllprevPregnancy,
  getAllprevProcedure,
  getProfileData,
} from "../services/ProfileService";
import { esc, printHtml, tableHtml } from "../utils/printDoc";
import { displayValue, sectionsFor, visibleFields } from "./MedicalHistory Component/questionnaireFields";
import { toRow } from "./MedicalHistory Component/TreatmentWithUs";
import { readEntries } from "./vitalsStore";

// ── My summary ───────────────────────────────────────────────────────────────
//
// Everything the clinic holds about the patient that is theirs to see, on one
// page laid out to be read and printed: personal details, partner, GP,
// allergies, vitals, medical history, previous treatment, pregnancies and
// procedures, current prescriptions, treatments and upcoming appointments.
// It replaces the old accordion "Profile Preview" in the menu. The old page is
// still at /ProfilePreview; the "submit and lock your profile" step it carried
// is here too, at the foot.
//
// Clinic notes, activity notes and unapproved results are never part of it.

const blank = (v) => v === null || v === undefined || ["", "n/a", "null", "undefined"].includes(String(v).trim().toLowerCase());
const day = (raw) => {
  if (blank(raw) || String(raw).startsWith("0001-01-01")) return "";
  const m = moment(raw);
  return m.isValid() && m.year() > 1 ? m.format("DD-MMM-YYYY") : "";
};
const join = (...parts) => parts.filter((p) => !blank(p)).join(", ");

const RELATIONSHIP = {
  1: "Legally single", 2: "Separated", 3: "Married / civil partner to the person having treatment",
  4: "Married / civil partner to another person", 5: "Divorced / partnership dissolved", 6: "Widowed / surviving civil partner",
};

function buildSections({ profile: p, partner: r, q, allergies, fertility, pregnancies, procedures, prescriptions, cycles, appointments, entries }) {
  const gender = String(localStorage.getItem("PatientGender")) === "1" ? "male" : "female";
  const out = [];

  out.push({
    title: "Personal details",
    rows: [
      ["Name", join(p?.firstName, p?.lastName)],
      ["Preferred name", p?.preferredName],
      ["Date of birth", p?.birthday ? `${day(p.birthday)}${blank(p?.age) ? "" : ` (${p.age})`}` : ""],
      ["Sex", p?.gendercodeName],
      ["NHS / CHI number", p?.nhsNumber],
      ["Email", p?.email],
      ["Mobile", p?.mobileNumber],
      ["Home phone", p?.homePhone],
      ["Address", join(p?.street, p?.city, p?.state, p?.postalCode, p?.country)],
      ["Occupation", p?.occupation],
      ["Ethnicity", p?.ethnicity],
      ["Relationship", RELATIONSHIP[p?.relationShipStatus]],
      ["Emergency contact", [p?.emergencyContactName, p?.emergencyContactRelationship && `(${p.emergencyContactRelationship})`, p?.emergencyContactTelephone].filter((x) => !blank(x)).join(" ")],
    ],
  });

  if (r) {
    out.push({
      title: "Partner",
      rows: [
        ["Name", r.fullname || join(r.firstname, r.lastname)],
        ["Date of birth", r.birthdate ? `${day(r.birthdate)}${blank(r.age) ? "" : ` (${r.age})`}` : ""],
        ["Sex", r.gender || r.gendercodeName],
        ["Email", r.emailaddress1],
        ["Mobile", r.mobilephone],
        ["Address", join(r.address1_line1, r.address1_city, r.address1_stateorprovince, r.address1_country)],
        ["Occupation", r.occupation],
      ],
    });
  }

  out.push({
    title: "General practitioner",
    rows: [
      ["GP", p?.gPsName],
      ["Surgery", p?.gpSurgeryName],
      ["Telephone", p?.gpTelephoneNumber],
      ["Email", p?.gp_email],
      ["Address", join(p?.gpStreet, p?.gpCity, p?.gpState, p?.gpPostalCode, p?.gpCountry)],
    ],
  });

  out.push({
    title: "Allergies",
    rows: allergies.length ? allergies.map((a) => [a.name, a.description]) : [["Known allergies", "None recorded"]],
  });

  const latest = (type) => entries.filter((e) => e.type === type).sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.value;
  const w = latest("weight") ?? q?.weightinKilograms;
  const h = latest("height") ?? q?.heightinMetres;
  const bmi = Number(w) > 0 && Number(h) > 0 ? (Number(w) / (Number(h) * Number(h))).toFixed(1) : "";
  const sys = latest("bpSystolic") ?? q?.systolic;
  const dia = latest("bpDiastolic") ?? q?.diastolic;
  out.push({
    title: "Vitals",
    rows: [
      ["Weight", Number(w) > 0 ? `${w} kg` : ""],
      ["Height", Number(h) > 0 ? `${h} m` : ""],
      ["BMI", bmi],
      ["Blood pressure", sys && dia ? `${sys}/${dia} mmHg` : ""],
      ["Oxygen saturation", latest("oxygen") ? `${latest("oxygen")}%` : ""],
      ["Heart rate", latest("heartRate") ? `${latest("heartRate")} bpm` : ""],
      ["Temperature", latest("temperature") ? `${latest("temperature")} °C` : ""],
    ],
  });

  // Medical history: only what has been answered.
  sectionsFor(gender).forEach((s) => {
    const rows = visibleFields(s, q, gender)
      .map((f) => [f.label, displayValue(f, q?.[f.name])])
      .filter(([, v]) => v !== null);
    if (rows.length) out.push({ title: s.title.replace(/^Section [A-Z]: /, "Medical history – "), rows });
  });

  if (fertility.length) {
    out.push({
      title: "Previous fertility treatment",
      table: {
        headers: ["Year", "Treatment", "Centre", "Eggs collected", "Embryos transferred", "Outcome"],
        rows: fertility.map((f) => [f.yearofTreatment, f.treatmentTypeName, f.centre, f.numberofEggsCollected, f.numberofEmbryosTransferred, f.outcomeName || f.outcome]),
      },
    });
  }
  if (pregnancies.length) {
    out.push({
      title: "Previous pregnancies",
      table: {
        headers: ["Year", "With current partner", "How conceived", "Weeks", "Comments"],
        rows: pregnancies.map((x) => [x.yearOfPregnancy, x.wasItWithPartner === true ? "Yes" : x.wasItWithPartner === false ? "No" : x.wasItWithPartner, x.conceptionName, x.numberOfWeeks, x.comments]),
      },
    });
  }
  if (procedures.length) {
    out.push({
      title: "Previous procedures",
      table: {
        headers: ["Procedure", "Date", "Anaesthetic", "Comments"],
        rows: procedures.map((x) => [x.procedureType, day(x.procedureDate), x.anaestheticType, x.comments]),
      },
    });
  }
  if (prescriptions.length) {
    out.push({
      title: "Prescriptions",
      table: {
        headers: ["Prescription", "Treatment", "Date", "Approved"],
        rows: prescriptions.map((x) => [x.name, x.treatmentName || x.prescriptionCycleName, day(x.createdOn), x.approved === false ? "No" : "Yes"]),
      },
    });
  }
  if (cycles.length) {
    out.push({
      title: "Treatments with us",
      table: {
        headers: ["Treatment", "Start", "Outcome"],
        rows: cycles.map((c) => [c.title, c.start ? c.start.format("DD-MMM-YYYY") : "", c.outcome]),
      },
    });
  }
  if (appointments.length) {
    out.push({
      title: "Upcoming appointments",
      table: {
        headers: ["When", "What", "Where", "Status"],
        rows: appointments.map((a) => [moment(a.startTime).format("ddd DD-MMM-YYYY HH:mm"), a.subject || a.appointmentlibraryitemName, a.roomName, a.bookingStatusName]),
      },
    });
  }
  return out;
}

const kept = (rows) => rows.filter(([, v]) => !blank(v));

export default function MySummary() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const url = process.env.REACT_APP_OXAR_API_URL;
  const uID = localStorage.getItem("UserID");
  const { show, menu } = useContextMenu();

  const [data, setData] = useState(null);
  const [profileRaw, setProfileRaw] = useState(null);

  const load = useCallback(async () => {
    dispatch(setLoader(true));
    const quiet = (p) => Promise.resolve(p).catch(() => null);
    const [pr, qu, rel, al, fe, pg, pc, rx, cy, ap] = await Promise.all([
      quiet(getProfileData(uID)),
      quiet(GetQuestionairebyPatientId(uID)),
      quiet(axios(`${url}api/CrmUser/GetRelatedContactByID?Id=${uID}`)),
      quiet(getAllergies(uID)),
      quiet(getAllfertilityTreat(uID)),
      quiet(getAllprevPregnancy(uID)),
      quiet(getAllprevProcedure(uID)),
      quiet(axios(`${url}api/Prescriptions/GetByContactId?id=${uID}`)),
      quiet(axios(`${url}api/Treatment/GetTreatmentCyclesByContactId?id=${uID}`)),
      quiet(axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${uID}`)),
    ]);
    const list = (x) => (Array.isArray(x?.content) ? x.content : []);
    const profile = pr?.content || null;
    setProfileRaw(profile);
    let couple = "";
    const partner = rel?.data?.content || null;
    try { couple = [[profile?.firstName, profile?.lastName].filter(Boolean).join(" "), partner?.fullname].filter(Boolean).join(" & "); } catch { couple = ""; }
    setData({
      profile,
      partner,
      q: qu?.content || null,
      allergies: list(al),
      fertility: list(fe),
      pregnancies: list(pg),
      procedures: list(pc),
      prescriptions: Array.isArray(rx?.data?.content) ? rx.data.content : [],
      cycles: (cy?.data?.content?.treatmentcycleDTO || []).map((c) => toRow(c, undefined, couple)).sort((a, b) => (b.start ? b.start.valueOf() : 0) - (a.start ? a.start.valueOf() : 0)),
      appointments: (Array.isArray(ap?.data?.content) ? ap.data.content : [])
        .filter((a) => moment(a.startTime).isSameOrAfter(moment().startOf("day")))
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)),
      entries: readEntries(),
    });
    dispatch(setLoader(false));
  }, [dispatch, uID, url]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => (data ? buildSections(data) : []), [data]);

  const print = () => {
    const name = join(data?.profile?.firstName, data?.profile?.lastName);
    const body = sections.map((s) => {
      if (s.table) {
        return `<h2>${esc(s.title)}</h2>${tableHtml(s.table.headers, s.table.rows.map((r) => r.map((c) => (blank(c) ? "" : c))))}`;
      }
      const rows = kept(s.rows);
      return rows.length ? `<h2>${esc(s.title)}</h2>${tableHtml(["", ""], rows.map(([k, v]) => [{ html: `<strong>${esc(k)}</strong>` }, v]))}` : "";
    }).join("");
    if (!printHtml({ title: "My summary", subtitle: name, bodyHtml: body || '<p class="muted">Nothing to print yet.</p>' })) {
      toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
    }
  };

  const submitProfile = async () => {
    if (!profileRaw || profileRaw.areYousureYouwanttosubmit !== false) return;
    try {
      dispatch(setLoader(true));
      const body = { ...profileRaw, areYousureYouwanttosubmit: true };
      const res = await axios.put(`${url}/api/Profile/Update`, body);
      if (res.status === 200) toast.success("Profile submitted successfully", { autoClose: 5000, hideProgressBar: true });
      const fresh = await getProfileData(uID);
      setProfileRaw(fresh?.content || body);
    } catch {
      toast.error("Your profile could not be submitted. Please try again.");
    } finally {
      dispatch(setLoader(false));
    }
  };

  const p = data?.profile;

  return (
    <div className="ox-mh ox-summary" onContextMenu={(e) => show(e, [
      { label: "Print my summary", onClick: print },
      { label: "Refresh", onClick: load },
    ])}>
      <div className="ox-mh__banner">
        <div>
          <h2 className="ox-mh__title">My summary</h2>
          <p className="ox-mh__sub">
            {p ? [join(p.firstName, p.lastName), p.birthday && `DOB ${day(p.birthday)}`, p.gendercodeName].filter(Boolean).join(" · ") : ""}
          </p>
        </div>
        <div className="ox-mh__actions">
          <button type="button" className="ox-btn ox-btn--primary" onClick={print} disabled={!data}>Print</button>
          <button type="button" className="ox-btn" onClick={load}>Refresh</button>
        </div>
      </div>
      <p className="ox-hint">Everything your clinic holds about you that you can see, in one place to read or print. To change something, open that part of the portal.</p>

      <div className="ox-mh__cols">
        {sections.map((s) => (
          <section key={s.title} className="ox-card">
            <header className="ox-card__head"><h3>{s.title}</h3></header>
            {s.table ? (
              <div className="ox-below__scroll">
                <table className="ox-mini">
                  <thead><tr>{s.table.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {s.table.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{blank(c) ? "—" : String(c)}</td>)}</tr>)}
                  </tbody>
                </table>
              </div>
            ) : (
              <dl className="ox-card__rows">
                {kept(s.rows).map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>))}
                {kept(s.rows).length === 0 && <p className="ox-hint">Nothing recorded yet.</p>}
              </dl>
            )}
          </section>
        ))}
      </div>

      {profileRaw && profileRaw.areYousureYouwanttosubmit === false && (
        <section className="ox-card ox-summary__submit">
          <p><b>Click to submit and lock your profile</b> once you are happy that what is above is correct.</p>
          <button type="button" className="ox-btn ox-btn--primary" onClick={submitProfile}>Submit</button>
        </section>
      )}
      {menu}
    </div>
  );
}
